// Threat Monitor — physical-threat findings (facility / protest / incitement /
// travel / event) plus vaccine-initiative impersonation. Both surfaces are
// keyword-baseline + Phase 2 NOVA classifier + Phase 4 paging-on-High.
import React, { useEffect, useState } from 'react';
import {
    bp_physical_threats, bp_vaccine_initiatives, bp_vaccine_safety_misinfo,
    bp_translate_mention,
} from '../../../services';
import { formatNumber, unwrap, EmptyState, Skeleton, SEVERITY_BORDER, timeAgo } from './_shared';
import { Icon } from './_icons';
import './brand-mentions.scss';

// Light-theme palette (2026-05-27 fix). The old values were dark-theme
// pastels (#fecaca etc.) — light text on a light tint = invisible on the
// white card. Now: 700-tier saturated text on a soft tint, readable.
const SEVERITY_COLOR = {
    Critical: { fg: '#7f1d1d', bg: 'rgba(127, 29, 29, 0.10)', border: 'rgba(127, 29, 29, 0.50)' },
    High:     { fg: '#b91c1c', bg: 'rgba(220, 38, 38, 0.10)', border: 'rgba(220, 38, 38, 0.50)' },
    Medium:   { fg: '#b45309', bg: 'rgba(249, 115, 22, 0.12)', border: 'rgba(249, 115, 22, 0.50)' },
    Low:      { fg: '#a16207', bg: 'rgba(234, 179, 8, 0.14)', border: 'rgba(234, 179, 8, 0.50)' },
};

function SevPill({ severity }) {
    const c = SEVERITY_COLOR[severity] || SEVERITY_COLOR.Low;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 10px', borderRadius: 4,
            fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
            textTransform: 'uppercase',
            color: c.fg, background: c.bg, border: `1px solid ${c.border}`,
        }}>{severity || '-'}</span>
    );
}

function NovaChip({ nova }) {
    if (!nova) return null;
    // Only confirmed AI verdicts carry a NOVA chip. Unconfirmed findings
    // already show a "needs review" badge; surfacing a heuristic confidence
    // here would read as a verdict it isn't.
    if (nova.provider !== 'bedrock_claude') return null;
    const conf = Number(nova.confidence || 0);
    const pct = Math.round(conf * 100);
    const rationale = nova.rationale ? `\n${nova.rationale}` : '';
    const evidence = nova.evidence_quote ? `\nEvidence: “${nova.evidence_quote}”` : '';
    return (
        <span title={`NOVA AI — ${nova.label || 'unclassified'} · confidence ${pct}%${rationale}${evidence}`}
              style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', borderRadius: 4,
                  fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
                  cursor: 'help', marginLeft: 6,
                  color: '#1d4ed8',
                  background: 'rgba(59,130,246,0.12)',
                  border: '1px solid rgba(59,130,246,0.45)',
              }}>
            NOVA AI · {pct}%
        </span>
    );
}

function LangChip({ lang, translatedFrom }) {
    if (!lang) return null;
    const label = translatedFrom ? `${translatedFrom}→en` : lang;
    return (
        <span style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 4,
            fontSize: 10, fontWeight: 600, letterSpacing: 0.4,
            color: '#475569', background: 'rgba(148, 163, 184, 0.15)',
            border: '1px solid rgba(148, 163, 184, 0.35)', marginLeft: 6,
        }}>{label}</span>
    );
}

// ISO-3166 → emoji-flag via the unicode regional-indicator trick.
// US → 🇺🇸, GB → 🇬🇧, etc. Returns null for 'XX' or invalid 2-letter codes
// so the chip can be hidden when geo signal is absent.
function flagFromIso(iso) {
    if (!iso || iso.length !== 2 || iso === 'XX') return null;
    const up = iso.toUpperCase();
    if (!/^[A-Z]{2}$/.test(up)) return null;
    return String.fromCodePoint(...[...up].map(c => 0x1f1e6 + c.charCodeAt(0) - 65));
}

function CountryChip({ iso, name, signal, count }) {
    const flag = flagFromIso(iso);
    if (!flag) return null;
    const tooltip = signal
        ? `${name || iso}${count ? ` · ${count} mentions` : ''} · inferred via ${signal}`
        : (name || iso);
    return (
        <span title={tooltip} style={{
            display: 'inline-block', padding: '2px 6px', borderRadius: 4,
            fontSize: 10, fontWeight: 600,
            color: '#475569', background: 'rgba(148, 163, 184, 0.12)',
            border: '1px solid rgba(148, 163, 184, 0.30)', marginLeft: 6,
        }}>
            <span style={{ marginRight: 4 }}>{flag}</span>
            {iso}{count ? ` ${count}` : ''}
        </span>
    );
}

// Best-practice response guidance per threat category. Mirrors how
// physical-security / threat-intel teams (Recorded Future, ZeroFox,
// corporate GSOC) run a finding: triage → assess → escalate to the
// right owner → respond → document. Guidance is advisory text — it
// doesn't auto-action, because the response owner and the right call
// are human decisions.
const RESPONSE_PLAYBOOK = {
    exec_travel_targeting: {
        owner: 'Executive Protection / Physical Security',
        steps: [
            'Verify the exec’s travel details aren’t publicly exposed (itinerary, hotel, routing).',
            'Brief the protection detail; consider varying routing or timing if the threat is credible.',
            'Cross-check the author against known-hostile actors and prior incidents.',
        ],
    },
    travel_chatter: {
        owner: 'Physical Security (monitor)',
        steps: [
            'Confirm whether the chatter references a real upcoming trip vs. historical/PR mention.',
            'No action if benign context; intensify monitoring if hostile sentiment appears.',
        ],
    },
    facility_targeting: {
        owner: 'Facility Security + Local Law Enforcement liaison',
        steps: [
            'Alert on-site security at the named facility; review access controls and perimeter.',
            'If an organised protest/blockade is indicated, coordinate with local law enforcement.',
            'Stage comms in case of disruption.',
        ],
    },
    protest_mobilisation: {
        owner: 'Security + Comms',
        steps: [
            'Assess scale, date, and location of the organising signal.',
            'Brief comms for a holding statement; adjust facility/event posture if a site is named.',
            'Monitor for escalation toward incitement.',
        ],
    },
    activist_targeting: {
        owner: 'Security + Comms',
        steps: [
            'Identify the campaign/group and its history with the organisation.',
            'Decide engage-vs-monitor with comms; preserve evidence of any threats.',
        ],
    },
    incitement: {
        owner: 'Security + Legal + Law Enforcement (urgent)',
        steps: [
            'Escalate immediately — do not wait for the scheduled review cycle.',
            'Preserve evidence (screenshots, URLs, author handles, timestamps).',
            'Report to the platform’s threat/abuse channel; involve law enforcement if credible or imminent.',
        ],
    },
    event_targeting: {
        owner: 'Event Security + travelling staff',
        steps: [
            'Coordinate with the event’s security team for the date window.',
            'Brief travelling staff; heighten monitoring before/during the event.',
        ],
    },
    vaccine_initiative_impersonation: {
        owner: 'Legal / Brand + Comms',
        steps: [
            'File a platform takedown for the impersonating account/site.',
            'Issue a public clarification and alert affected partners/donors.',
        ],
    },
    vaccine_safety_misinfo: {
        owner: 'Medical Affairs + Comms',
        steps: [
            'Draft a factual rebuttal grounded in the WHO/CDC corpus.',
            'Report to the platform; track spread and reach before deciding on public response.',
        ],
    },
};

function ResponsePlaybook({ subcategory, severity }) {
    const pb = RESPONSE_PLAYBOOK[subcategory];
    if (!pb) return null;
    const urgent = severity === 'High' || severity === 'Critical' || subcategory === 'incitement';
    return (
        <div style={{
            marginTop: 12, padding: '10px 12px', borderRadius: 6,
            background: urgent ? 'rgba(220,38,38,0.05)' : 'rgba(248,250,252,0.8)',
            border: `1px solid ${urgent ? 'rgba(220,38,38,0.30)' : 'rgba(148,163,184,0.30)'}`,
            borderLeft: `3px solid ${urgent ? '#dc2626' : '#3b82f6'}`,
        }}>
            <div style={{
                fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                color: urgent ? '#b91c1c' : '#475569', marginBottom: 6,
                display: 'flex', alignItems: 'center', gap: 6,
            }}>
                <Icon name={urgent ? 'alert' : 'shield'} size={12} />
                Recommended response
                <span style={{
                    marginLeft: 6, fontWeight: 600, textTransform: 'none', letterSpacing: 0,
                    color: '#64748b',
                }}>
                    · owner: {pb.owner}
                </span>
            </div>
            <ol style={{ margin: 0, paddingLeft: 18, color: '#334155', fontSize: 11.5, lineHeight: 1.6 }}>
                {pb.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
        </div>
    );
}

// Customer-facing "how detection works" explainer. Collapsible so it
// doesn't crowd daily use, expanded on first read.
function MethodologyPanel() {
    const [open, setOpen] = useState(false);
    return (
        <div style={{
            marginBottom: 14, border: '1px solid rgba(148,163,184,0.30)',
            borderRadius: 10, background: '#ffffff', overflow: 'hidden',
        }}>
            <button type="button" onClick={() => setOpen(o => !o)}
                    style={{
                        width: '100%', textAlign: 'left', cursor: 'pointer',
                        padding: '12px 16px', background: 'rgba(248,250,252,0.8)',
                        border: 'none', display: 'flex', alignItems: 'center', gap: 8,
                        fontSize: 13, fontWeight: 600, color: '#0f172a',
                    }}>
                <Icon name="chevron" size={14} color="#64748b"
                      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }} />
                <Icon name="shield" size={15} color="#2563eb" />
                How we identify threats
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: '#64748b' }}>
                    methodology · 4-stage pipeline
                </span>
            </button>
            {open && (
                <div style={{ padding: '14px 18px', fontSize: 12, color: '#334155', lineHeight: 1.65 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 14 }}>
                        {[
                            { n: 1, t: 'Capture', d: 'Every brand mention the scanner collects across X, Reddit, Facebook, LinkedIn, news, blogs, and forums is fed in — Threat Monitor re-reads that pool, it doesn’t fetch separately.' },
                            { n: 2, t: 'Keyword + watchlist baseline', d: 'High-recall first pass: matches threat vocabulary (protest / incitement / travel / activist terms) and your configured facility, event, and exec-travel watchlists.' },
                            { n: 3, t: 'NOVA AI confirmation', d: 'Each keyword hit is sent to NOVA AI for semantic intent classification — separating a real threat from benign context (e.g. “launched at Davos” vs “targeting the exec at Davos”).' },
                            { n: 4, t: 'Corroboration + severity', d: 'A finding is promoted only with hostile corroboration (negative sentiment, risk-tagged author, incitement language, or NOVA AI confirmation). Severity is assigned, and confirmed High/Critical findings auto-page on-call.' },
                        ].map(s => (
                            <div key={s.n} style={{
                                padding: '10px 12px', borderRadius: 8,
                                background: 'rgba(248,250,252,0.9)',
                                border: '1px solid rgba(148,163,184,0.25)',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                                    <span style={{
                                        width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                                        background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700,
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    }}>{s.n}</span>
                                    <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 12 }}>{s.t}</span>
                                </div>
                                <div style={{ fontSize: 11, color: '#475569' }}>{s.d}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ fontSize: 11, color: '#64748b' }}>
                        <b>Three surfaces monitored:</b> physical / activist targeting (facility, protest, incitement, exec-travel, event) ·
                        vaccine-initiative impersonation (against your watchlist) · vaccine-safety misinformation (scored against the WHO + CDC false-claim corpus).
                    </div>
                </div>
            )}
        </div>
    );
}

// Business-risk rationale per subcategory — customer-facing prose explaining
// *why* this kind of finding warrants attention. Pairs with the
// API-driven `i_button` (the detection method) inside <WhyHowPanel>.
const WHY_THIS_IS_A_RISK = {
    incitement: 'Explicit calls for violence, intimidation, or harm directed at your people, partners, or facilities. Even unverified threats create duty-of-care exposure and demand immediate triage to keep staff safe.',
    exec_travel_targeting: 'Posts referencing an exec’s upcoming travel narrow the window of physical access risk. Adversaries use these signals to plan protests, ambushes, or unwanted contact at known waypoints (hotel, route, venue).',
    travel_chatter: 'Leadership-travel chatter (Davos / WEF / WHO summit / etc.) raises ambient exposure during a fixed window. Most is benign news framing; hostile sentiment is the tell that converts chatter into a security signal.',
    facility_targeting: 'Mentions naming a specific facility can indicate reconnaissance, protest planning, or grievance focused on that site. Early visibility lets local security adjust posture before an event materialises.',
    protest_mobilisation: 'Posts organising or mobilising protest activity against your brand. Scale, date, and location signals let security and comms decide whether to engage, monitor, or harden the named site.',
    activist_targeting: 'Coordinated activist campaigns targeting the organisation. Repeat campaigns track over time; a sudden spike usually precedes a public-facing action and warrants comms engagement.',
    event_targeting: 'Posts referencing a watched event within its date window — protest, disruption, or targeting risk. Acting inside the window protects staff and attendees before the event itself is impacted.',
    vaccine_initiative_impersonation: 'Impersonating accounts or look-alike sites tied to your vaccine initiatives deceive partners, donors, and the public. Left up, they erode trust and divert traffic from authoritative channels.',
    vaccine_safety_misinfo: 'Posts asserting your vaccines cause harm, are unsafe, or fall under conspiracy narratives. Risk: erosion of public confidence in immunisation programmes, donor reputation impact, secondary effects on partner uptake. The 24h detection SLA matters here — early intervention limits the audience that ever sees the claim.',
};

// Compute the "how we detected this" bullet points from the finding's evidence.
// All inputs come from the API — counts, samples, NOVA verdicts — so the
// bullets describe *this specific finding*, not generic copy.
function detectionBullets(f) {
    const ev = f.evidence || {};
    const samples = ev.samples || [];
    const bullets = [];

    if (ev.count > 0) {
        bullets.push(`${formatNumber(ev.count)} mention${ev.count === 1 ? '' : 's'} in the lookback window matched the keyword / pattern baseline.`);
    }
    if (ev.risk_tagged_authors > 0) {
        bullets.push(`${ev.risk_tagged_authors} mention${ev.risk_tagged_authors === 1 ? '' : 's'} came from authors on your risk-tagged source watchlist — that alone clears the hostile-corroboration gate even when sentiment looks benign.`);
    }
    const novaConfirmed = samples.filter(s => s?.nova?.provider === 'bedrock_claude');
    if (novaConfirmed.length > 0) {
        const avg = novaConfirmed.reduce((sum, s) => sum + Number(s.nova.confidence || 0), 0) / novaConfirmed.length;
        const pct = Math.round(avg * 100);
        bullets.push(`NOVA AI confirmed the threat label on ${novaConfirmed.length} sample${novaConfirmed.length === 1 ? '' : 's'} (avg confidence ${pct}%) — this is what promoted the cluster from keyword candidate to a confirmed finding.`);
    }
    const translated = samples.filter(s => s?.nova?.translated_from);
    if (translated.length > 0) {
        bullets.push(`${translated.length} sample${translated.length === 1 ? '' : 's'} ${translated.length === 1 ? 'was' : 'were'} originally non-English; NOVA translated to English before re-running the pattern match (catches paraphrases the source-language regex would miss).`);
    }
    return bullets;
}

function WhyHowPanel({ f }) {
    // Prefer the per-finding prose the backend now ships (`why_this_is_a_risk`
    // and `how_we_detected_this`) so each card describes its own evidence
    // rather than a generic per-subcategory blurb. Fall back to the local
    // copy for older payloads or subcategories the backend hasn't filled.
    const why = f.why_this_is_a_risk || WHY_THIS_IS_A_RISK[f.subcategory];
    const apiHow = f.how_we_detected_this;
    const how = (apiHow && apiHow.method) || f.i_button;
    const bullets = (apiHow && Array.isArray(apiHow.bullets) && apiHow.bullets.length > 0)
        ? apiHow.bullets
        : detectionBullets(f);
    if (!why && !how && bullets.length === 0) return null;

    const c = SEVERITY_COLOR[f.severity] || SEVERITY_COLOR.High;

    return (
        <div style={{
            margin: '10px 0 14px',
            padding: '12px 14px',
            border: `1px solid ${c.border}`,
            background: c.bg,
            borderRadius: 6,
        }}>
            {why && (
                <>
                    <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                        textTransform: 'uppercase', color: c.fg, marginBottom: 6,
                    }}>
                        Why this is a risk
                    </div>
                    <p style={{
                        margin: 0, fontSize: 12, lineHeight: 1.6, color: '#334155',
                    }}>{why}</p>
                </>
            )}
            {(how || bullets.length > 0) && (
                <>
                    <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                        textTransform: 'uppercase', color: c.fg,
                        marginTop: why ? 12 : 0, marginBottom: 6,
                    }}>
                        How we detected this
                    </div>
                    {how && (
                        <p style={{
                            margin: 0, fontSize: 12, lineHeight: 1.6,
                            color: '#334155', fontStyle: 'italic',
                        }}>{how}</p>
                    )}
                    {bullets.length > 0 && (
                        <ul style={{
                            margin: how ? '8px 0 0' : 0,
                            paddingLeft: 18,
                            color: '#334155', fontSize: 12, lineHeight: 1.65,
                        }}>
                            {bullets.map((b, i) => <li key={i}>{b}</li>)}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}

function FindingCard({ f, onDismiss }) {
    const ev = f.evidence || {};
    const samples = ev.samples || [];
    const countries = ev.countries || [];
    // Expand High/Critical by default; collapse Medium/Low so the page
    // isn't a wall. Click the header to toggle.
    const [open, setOpen] = useState(f.severity === 'High' || f.severity === 'Critical');
    return (
        <div className="bm_topic_card" style={{ borderLeft: `3px solid ${SEVERITY_BORDER[f.severity] || SEVERITY_BORDER.Info}` }}>
            <div className="bm_topic_head"
                 onClick={() => setOpen(o => !o)}
                 title={open ? 'Collapse' : 'Expand details'}
                 style={{ alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
                <Icon name="chevron" size={14}
                      color="#64748b"
                      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms' }} />
                <SevPill severity={f.severity} />
                {f.review_status === 'needs_review' && (
                    <span title="Surfaced for analyst review — not yet confirmed or escalated to on-call."
                          style={{
                              padding: '2px 9px', borderRadius: 4,
                              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                              textTransform: 'uppercase',
                              color: '#b45309', background: 'rgba(245,158,11,0.15)',
                              border: '1px solid rgba(245,158,11,0.55)',
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                          }}>
                        <Icon name="clock" size={11} /> needs review
                    </span>
                )}
                {f.review_status === 'confirmed' && (f.severity === 'High' || f.severity === 'Critical') && (
                    <span title="Confirmed by NOVA AI or a risk-tagged author — paged to on-call."
                          style={{
                              padding: '2px 9px', borderRadius: 4,
                              fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                              textTransform: 'uppercase',
                              color: '#b91c1c', background: 'rgba(220,38,38,0.12)',
                              border: '1px solid rgba(220,38,38,0.55)',
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                          }}>
                        <Icon name="bell" size={11} /> paged
                    </span>
                )}
                <span className="bm_topic_title">{f.description}</span>
                {f.subcategory && (
                    <span style={{
                        fontSize: 10, color: '#64748b', background: 'rgba(148,163,184,0.15)',
                        padding: '2px 8px', borderRadius: 4, letterSpacing: 0.4,
                        textTransform: 'uppercase', fontWeight: 600,
                    }}>{String(f.subcategory).replace(/_/g, ' ')}</span>
                )}
                {/* Collapsed summary + dismiss live on the right of the head. */}
                <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12 }}>
                    {!open && (
                        <span style={{ fontSize: 11, color: '#64748b' }}>
                            {ev.count || 0} mention{ev.count === 1 ? '' : 's'}
                        </span>
                    )}
                    {onDismiss && (
                        <button type="button"
                                onClick={(e) => { e.stopPropagation(); onDismiss(f); }}
                                title="Dismiss — mark this as not a threat for this session"
                                style={{
                                    border: '1px solid rgba(148,163,184,0.4)', background: '#fff',
                                    color: '#64748b', borderRadius: 4, padding: '2px 8px',
                                    fontSize: 11, cursor: 'pointer',
                                }}>
                            Dismiss
                        </button>
                    )}
                </span>
            </div>
            {open && (
                <>
                    <WhyHowPanel f={f} />
                    <div className="bm_topic_metrics">
                        {ev.count != null && (
                            <div className="bm_topic_metric"><label>Mentions</label><span className="v">{formatNumber(ev.count)}</span></div>
                        )}
                        {ev.risk_tagged_authors != null && (
                            <div className="bm_topic_metric"><label>Risk-tagged authors</label><span className="v">{ev.risk_tagged_authors}</span></div>
                        )}
                        {ev.target && ev.target !== 'general' && (
                            <div className="bm_topic_metric"><label>Target</label><span className="v">{ev.target}</span></div>
                        )}
                        {ev.initiative && (
                            <div className="bm_topic_metric"><label>Initiative</label><span className="v">{ev.initiative}</span></div>
                        )}
                        {countries.length > 0 && (
                            <div className="bm_topic_metric" style={{ gridColumn: '1 / -1' }}>
                                <label>Geo footprint</label>
                                <span className="v" style={{ fontSize: 13, display: 'flex',
                                                              flexWrap: 'wrap', gap: 4 }}>
                                    {countries.slice(0, 8).map(c => (
                                        <CountryChip key={c.iso} iso={c.iso} name={c.name}
                                                     count={c.count} />
                                    ))}
                                    {countries.length > 8 && (
                                        <span style={{ fontSize: 11, color: '#64748b',
                                                       alignSelf: 'center', marginLeft: 4 }}>
                                            +{countries.length - 8} more
                                        </span>
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                    {samples.length > 0 && (
                        <div style={{ marginTop: 10, fontSize: 12 }}>
                            {samples.slice(0, 5).map((s, i) => (
                                <SampleRow key={s.id || i} s={s} />
                            ))}
                        </div>
                    )}
                    {/* Best-practice response guidance for this threat type. */}
                    <ResponsePlaybook subcategory={f.subcategory} severity={f.severity} />
                </>
            )}
        </div>
    );
}

// One sample mention inside a finding. Click "Translate to En" to swap the
// preview text in place for the English translation. "Open mention" still
// opens the source URL (the whole row stays a link).
function SampleRow({ s }) {
    const [translation, setTranslation] = useState('');
    const [translating, setTranslating] = useState(false);
    const [translateError, setTranslateError] = useState('');

    const translate = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (translating || translation) return;
        const id = s.id || s.mention_id;
        if (!id) {
            setTranslateError('Translation not available for this mention.');
            return;
        }
        setTranslating(true);
        setTranslateError('');
        try {
            const res = await bp_translate_mention(id);
            const d = unwrap(res) || {};
            if (d.translated_text) {
                setTranslation(d.translated_text);
            } else {
                setTranslateError(d.error === 'translation_unavailable'
                    ? 'NOVA AI is currently unavailable.'
                    : 'Translation failed.');
            }
        } catch (err) {
            setTranslateError('Translation failed.');
        } finally {
            setTranslating(false);
        }
    };

    const displayText = translation || s.text_preview || s.text || '(no preview)';

    return (
        <a href={s.url || undefined}
           target={s.url ? '_blank' : undefined}
           rel="noopener noreferrer"
           style={{
               display: 'block', textDecoration: 'none',
               marginBottom: 6, padding: '8px 10px',
               borderLeft: '2px solid #e2e8f0',
               borderRadius: '0 6px 6px 0',
               background: 'rgba(248,250,252,0.7)',
               cursor: s.url ? 'pointer' : 'default',
               transition: 'background 100ms',
           }}
           onMouseEnter={e => e.currentTarget.style.background = 'rgba(241,245,249,1)'}
           onMouseLeave={e => e.currentTarget.style.background = 'rgba(248,250,252,0.7)'}>
            <div style={{ color: '#0f172a', fontSize: 11, fontWeight: 600,
                          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                {s.handle} <span style={{ color: '#94a3b8' }}>· {s.platform}</span>
                <NovaChip nova={s.nova} />
                <LangChip lang={s.nova?.language} translatedFrom={s.nova?.translated_from} />
                <CountryChip iso={s.country_iso} name={s.country_name}
                             signal={s.country_signal} />
            </div>
            <div style={{ color: '#475569', fontSize: 11, marginTop: 3 }}>
                {displayText}
            </div>
            {translateError && (
                <div style={{ color: '#b91c1c', fontSize: 10, marginTop: 3 }}>
                    {translateError}
                </div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 }}>
                {s.url && (
                    <span style={{ fontSize: 10, color: '#2563eb', fontWeight: 600 }}>
                        Open mention ↗
                    </span>
                )}
                {!translation && (
                    <span role="button"
                          tabIndex={0}
                          onClick={translate}
                          onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') translate(e);
                          }}
                          style={{
                              fontSize: 10, fontWeight: 600,
                              color: translating ? '#94a3b8' : '#2563eb',
                              cursor: translating ? 'wait' : 'pointer',
                          }}>
                        {translating ? 'Translating…' : 'Translate to En'}
                    </span>
                )}
            </div>
        </a>
    );
}

// Severity dots for the posture card — count + colored swatch.
function SeverityDot({ count, label, color, dim }) {
    return (
        <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            opacity: dim && count === 0 ? 0.45 : 1,
        }}>
            <span style={{
                width: 10, height: 10, borderRadius: '50%',
                background: count > 0 ? color : '#cbd5e1',
                boxShadow: count > 0 ? `0 0 0 3px ${color}22` : 'none',
            }} />
            <span style={{ fontSize: 16, fontWeight: 700, color: '#0f172a',
                           fontVariantNumeric: 'tabular-nums' }}>{count}</span>
            <span style={{ fontSize: 11, color: '#64748b' }}>{label}</span>
        </span>
    );
}

// One row in the "surfaces checked" list — either a warning (has findings)
// or a reassuring "clear" tick. The whole point: an analyst can tell
// "scanned and found nothing" (good) from "not monitored" (unknown).
function SurfaceStatusRow({ label, count, sub }) {
    const clear = count === 0;
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '6px 0', fontSize: 12,
        }}>
            <span style={{
                width: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: clear ? '#15803d' : '#b45309',
            }}>
                <Icon name={clear ? 'check' : 'alert'} size={13} />
            </span>
            <span style={{ flex: 1, color: '#0f172a', fontWeight: 500 }}>{label}</span>
            <span style={{
                fontSize: 11, fontWeight: 600,
                color: clear ? '#15803d' : '#b45309',
            }}>
                {clear ? 'clear' : `${count} finding${count === 1 ? '' : 's'}`}
            </span>
            {sub && <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>{sub}</span>}
        </div>
    );
}

// Compact reassurance strip shown in place of an empty findings section,
// so a clear surface reads as "monitored, nothing detected" rather than
// vanishing.
function ClearStrip({ title, note }) {
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '12px 14px', marginTop: 12,
            background: 'rgba(34,197,94,0.06)',
            border: '1px solid rgba(34,197,94,0.30)',
            borderLeft: '3px solid #16a34a',
            borderRadius: 6,
        }}>
            <span style={{ color: '#15803d', display: 'inline-flex' }}>
                <Icon name="check" size={16} />
            </span>
            <div>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a' }}>
                    {title} — clear
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 2 }}>
                    {note}
                </div>
            </div>
        </div>
    );
}

export default function ThreatMonitorTab() {
    const [days, setDays] = useState(30);
    const [physical, setPhysical] = useState(null);
    const [vaccine, setVaccine] = useState(null);
    const [vacSafety, setVacSafety] = useState(null);
    const [loading, setLoading] = useState(true);
    // Customizable view — severity filter + session dismissals.
    const [sevFilter, setSevFilter] = useState('all'); // all|Critical|High|Medium|Low
    const [dismissed, setDismissed] = useState(() => new Set());

    const dismissFinding = (f) => {
        // Stable key from description+subcategory+target.
        const key = `${f.description}|${f.subcategory}|${f.evidence?.target || ''}`;
        setDismissed(prev => {
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };
    const visibleFilter = (list) => (list || []).filter(f => {
        const key = `${f.description}|${f.subcategory}|${f.evidence?.target || ''}`;
        if (dismissed.has(key)) return false;
        if (sevFilter !== 'all' && f.severity !== sevFilter) return false;
        return true;
    });

    useEffect(() => {
        setLoading(true);
        Promise.allSettled([
            bp_physical_threats({ days }),
            bp_vaccine_initiatives({ days }),
            bp_vaccine_safety_misinfo({ days }),
        ]).then(([p, v, vs]) => {
            const ok = r => r?.status === 'fulfilled' ? r.value : null;
            setPhysical(unwrap(ok(p)) || {});
            setVaccine(unwrap(ok(v)) || {});
            setVacSafety(unwrap(ok(vs)) || {});
            setLoading(false);
        });
    }, [days]);

    const physFindings = (physical?.detailed_findings) || [];
    const vacFindings = (vaccine?.detailed_findings) || [];
    const vacSafetyFindings = (vacSafety?.detailed_findings) || [];
    const paging = physical?.paging || null;
    const totalFindings = physFindings.length + vacFindings.length + vacSafetyFindings.length;

    // Severity rollup across all three surfaces — drives the posture card.
    const sevCounts = { Critical: 0, High: 0, Medium: 0, Low: 0 };
    [...physFindings, ...vacFindings, ...vacSafetyFindings].forEach(f => {
        const s = f.severity || 'Low';
        if (sevCounts[s] != null) sevCounts[s] += 1;
    });
    const pagedCount = paging ? (paging.dispatched || 0) : 0;
    // Precision-gate rollup (physical-threat scanner only emits this).
    const review = physical?.review_summary || {};
    const needsReviewCount = review.needs_review || 0;
    const highCritNeedsReview = review.high_crit_needs_review || 0;
    // All three endpoints return the same org-wide all-time mention count.
    // Pick the first one that has it (physical_threats is the most reliable
    // since it's always fetched first).
    const totalAllTime = (
        physical?.total_all_time_mentions
        ?? vaccine?.total_all_time_mentions
        ?? vacSafety?.total_all_time_mentions
        ?? null
    );

    return (
        <>
            <div className="bm_header">
                <div>
                    <div className="bm_subtitle">
                        Threat Monitor · {totalFindings} findings
                        (last {days} days)
                        {totalAllTime != null && (
                            <span style={{
                                marginLeft: 8, fontSize: 12, color: '#94a3b8',
                                fontWeight: 400,
                            }}>
                                · analysed {totalAllTime} captured mention{totalAllTime === 1 ? '' : 's'}
                            </span>
                        )}
                    </div>
                    <div className="bm_subtitle" style={{ marginTop: 4, fontSize: 12 }}>
                        Activist targeting · protest mobilisation · threats / incitement ·
                        travel-related targeting · event chatter · vaccine-initiative impersonation ·
                        vaccine-safety misinformation (WHO+CDC corpus).
                        High / Critical findings auto-page via the configured provider.
                    </div>
                </div>
            </div>

            <div className="bm_filters" style={{ alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <select value={days} onChange={e => setDays(Number(e.target.value))}>
                    <option value={7}>Last 7 days</option>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
                {/* Severity filter — customizable view. */}
                {totalFindings > 0 && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="filter" size={12} color="#64748b" />
                        {['all', 'Critical', 'High', 'Medium', 'Low'].map(s => (
                            <button key={s} type="button"
                                    onClick={() => setSevFilter(s)}
                                    style={{
                                        padding: '3px 10px', borderRadius: 999,
                                        fontSize: 11, fontWeight: 600, cursor: 'pointer',
                                        textTransform: s === 'all' ? 'none' : 'capitalize',
                                        border: `1px solid ${sevFilter === s ? '#2563eb' : 'rgba(148,163,184,0.4)'}`,
                                        background: sevFilter === s ? 'rgba(37,99,235,0.10)' : '#fff',
                                        color: sevFilter === s ? '#1d4ed8' : '#475569',
                                    }}>
                                {s === 'all' ? 'All' : s}
                            </button>
                        ))}
                    </span>
                )}
                {dismissed.size > 0 && (
                    <button type="button" onClick={() => setDismissed(new Set())}
                            style={{ fontSize: 11, color: '#2563eb', background: 'none',
                                     border: 'none', cursor: 'pointer' }}>
                        Restore {dismissed.size} dismissed
                    </button>
                )}
                {paging && (
                    <span style={{
                        fontSize: 11, color: '#94a3b8', marginLeft: 'auto',
                        background: 'rgba(148,163,184,0.12)', padding: '4px 10px',
                        borderRadius: 4,
                    }}>
                        Paging · dispatched {paging.dispatched}/{paging.total}
                        {paging.failed > 0 && <span style={{ color: '#fca5a5' }}>  · failed {paging.failed}</span>}
                    </span>
                )}
            </div>

            {/* Customer-facing methodology explainer — always available. */}
            <MethodologyPanel />

            {loading && (
                <div style={{ display: 'grid', gap: 8, padding: '12px 0' }}>
                    <Skeleton height={80} />
                    <Skeleton height={80} />
                    <Skeleton height={80} />
                </div>
            )}

            {/* ---- All three surfaces empty: distinguish "nothing scanned */}
            {/* yet" from "scanned, all clear". If we analysed mentions but */}
            {/* found no threats, that's GOOD news — say so, don't show the */}
            {/* config-prompt empty state. */}
            {!loading && totalFindings === 0 && (
                (totalAllTime && totalAllTime > 0) ? (
                    <div style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        padding: '20px 22px',
                        background: 'rgba(34,197,94,0.06)',
                        border: '1px solid rgba(34,197,94,0.30)',
                        borderLeft: '4px solid #16a34a',
                        borderRadius: 10,
                    }}>
                        <span style={{
                            width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                            background: 'rgba(34,197,94,0.15)', color: '#15803d',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <Icon name="shield" size={22} />
                        </span>
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                                All clear — no threats detected
                            </div>
                            <div style={{ fontSize: 12.5, color: '#475569', marginTop: 4, lineHeight: 1.5 }}>
                                Analysed {formatNumber(totalAllTime)} captured mention{totalAllTime === 1 ? '' : 's'} over
                                the last {days} days across all three threat surfaces — physical / activist
                                targeting, vaccine-initiative impersonation, and vaccine-safety misinformation.
                                Nothing matched a watchlist entry or documented false-claim pattern.
                            </div>
                        </div>
                    </div>
                ) : (
                    <EmptyState
                        mark="bars"
                        title="No threat findings in this window"
                        body="Threat Monitor reads from the facility / event / exec-travel / vaccine-initiative watchlists plus the WHO+CDC vaccine-safety misinformation corpus. Findings appear once mentions match a watchlist entry or a documented false-claim pattern."
                        action={(
                            <a href="/brand-protection/mentions"
                               onClick={(e) => {
                                   e.preventDefault();
                                   window.dispatchEvent(new CustomEvent('bm-tab-switch', { detail: { tab: 'settings' } }));
                               }}
                               className="btn btn-sm btn-primary"
                               style={{ textDecoration: 'none' }}>
                                Go to Settings → Watchlists
                            </a>
                        )}
                    />
                )
            )}

            {/* ---- Threat posture summary — only when there's at least one */}
            {/* finding. Gives severity rollup + paging + per-surface status */}
            {/* so the analyst sees the whole picture before scrolling. */}
            {!loading && totalFindings > 0 && (
                <div style={{
                    padding: '16px 18px', marginBottom: 16,
                    background: '#ffffff',
                    border: '1px solid rgba(148,163,184,0.30)',
                    borderRadius: 10,
                    borderTop: `3px solid ${sevCounts.Critical > 0 ? '#7f1d1d' : sevCounts.High > 0 ? '#dc2626' : '#f97316'}`,
                }}>
                    <div style={{
                        fontSize: 10, fontWeight: 700, letterSpacing: 0.6,
                        textTransform: 'uppercase', color: '#64748b', marginBottom: 12,
                    }}>
                        Threat posture · last {days} days
                    </div>
                    <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                        <SeverityDot count={sevCounts.Critical} label="Critical" color="#7f1d1d" dim />
                        <SeverityDot count={sevCounts.High} label="High" color="#dc2626" dim />
                        <SeverityDot count={sevCounts.Medium} label="Medium" color="#f97316" dim />
                        <SeverityDot count={sevCounts.Low} label="Low" color="#eab308" dim />
                        <span style={{
                            marginLeft: 'auto', fontSize: 12, color: '#475569',
                        }}>
                            <b style={{ color: '#0f172a' }}>{totalFindings}</b> finding{totalFindings === 1 ? '' : 's'}
                            {pagedCount > 0 && (
                                <span style={{ marginLeft: 10, color: '#b91c1c', fontWeight: 600,
                                               display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <Icon name="bell" size={12} /> {pagedCount} paged to on-call
                                </span>
                            )}
                            {needsReviewCount > 0 && (
                                <span style={{ marginLeft: 10, color: '#b45309', fontWeight: 600,
                                               display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <Icon name="clock" size={12} /> {needsReviewCount} need review
                                </span>
                            )}
                        </span>
                    </div>
                    {/* Review-pending notice — these findings await analyst */}
                    {/* sign-off, so "not paged" isn't mistaken for "no threats". */}
                    {needsReviewCount > 0 && (
                        <div style={{
                            marginTop: 10, padding: '8px 12px', borderRadius: 6,
                            background: 'rgba(245,158,11,0.10)',
                            border: '1px solid rgba(245,158,11,0.45)',
                            fontSize: 11.5, color: '#92400e', lineHeight: 1.5,
                        }}>
                            <b>{needsReviewCount} finding{needsReviewCount === 1 ? '' : 's'} pending analyst review.</b>{' '}
                            Expand each finding below to confirm it as a genuine threat or
                            dismiss it. Confirmed High / Critical findings are escalated to
                            on-call{highCritNeedsReview > 0
                                ? ` (${highCritNeedsReview} High / Critical awaiting review)`
                                : ''}.
                        </div>
                    )}
                    <div style={{
                        marginTop: 10, paddingTop: 10,
                        borderTop: '1px solid rgba(148,163,184,0.25)',
                    }}>
                        <SurfaceStatusRow label="Physical & activist threats" count={physFindings.length} />
                        <SurfaceStatusRow label="Vaccine-initiative impersonation" count={vacFindings.length} />
                        <SurfaceStatusRow label="Vaccine-safety misinformation"
                                          count={vacSafetyFindings.length}
                                          sub={vacSafety?.corpus_version ? `corpus ${vacSafety.corpus_version}` : null} />
                    </div>
                </div>
            )}

            {/* ---- Surface 1: Physical & activist ---- */}
            {!loading && totalFindings > 0 && (
                physFindings.length > 0 ? (
                    <>
                        <div className="bm_section_title">
                            Physical &amp; activist threats <span className="bm_section_count">{physFindings.length}</span>
                        </div>
                        <div className="bm_topics_list">
                            {visibleFilter(physFindings).map((f, i) => <FindingCard key={'p' + i} f={f} onDismiss={dismissFinding} />)}
                        </div>
                    </>
                ) : (
                    <ClearStrip title="Physical &amp; activist threats"
                                note="No facility / protest / incitement / travel / event targeting matched this window." />
                )
            )}

            {/* ---- Surface 2: Vaccine-initiative impersonation ---- */}
            {!loading && totalFindings > 0 && (
                vacFindings.length > 0 ? (
                    <>
                        <div className="bm_section_title" style={{ marginTop: 16 }}>
                            Vaccine-initiative impersonation <span className="bm_section_count">{vacFindings.length}</span>
                        </div>
                        <div className="bm_topics_list">
                            {visibleFilter(vacFindings).map((f, i) => <FindingCard key={'v' + i} f={f} onDismiss={dismissFinding} />)}
                        </div>
                    </>
                ) : (
                    <ClearStrip title="Vaccine-initiative impersonation"
                                note="No accounts or sites impersonating a monitored vaccine initiative were detected." />
                )
            )}

            {/* ---- Surface 3: Vaccine-safety misinformation ---- */}
            {!loading && totalFindings > 0 && (
                vacSafetyFindings.length > 0 ? (
                    <>
                        <div className="bm_section_title" style={{ marginTop: 16 }}>
                            Vaccine-safety misinformation <span className="bm_section_count">{vacSafetyFindings.length}</span>
                            {vacSafety?.corpus_version && (
                                <span style={{
                                    marginLeft: 8, fontSize: 10, color: '#94a3b8',
                                    background: 'rgba(148,163,184,0.12)',
                                    padding: '2px 8px', borderRadius: 4,
                                    textTransform: 'uppercase', letterSpacing: 0.4,
                                    fontWeight: 600,
                                }}>
                                    corpus · {vacSafety.corpus_version}
                                </span>
                            )}
                        </div>
                        <div className="bm_topics_list">
                            {visibleFilter(vacSafetyFindings).map((f, i) => <FindingCard key={'vsm' + i} f={f} onDismiss={dismissFinding} />)}
                        </div>
                    </>
                ) : (
                    <ClearStrip title="Vaccine-safety misinformation"
                                note={`No mentions matched the WHO+CDC false-claim corpus${vacSafety?.corpus_version ? ` (${vacSafety.corpus_version})` : ''}.`} />
                )
            )}
        </>
    );
}
